/**
 * FIS Cleanup Utilities
 * =====================
 * Port of FIS data cleanup logic from v1.
 * Handles: FIS code validation, name/nation/YOB updates, points lookup.
 */

import { CANONICAL_FIELDS, getField } from './columnNormalizer';

// Lambda URLs
const FIS_POINTS_LAMBDA = 'https://ggpwjy2pju3cttzzmoxhnuidyi0bkuwd.lambda-url.us-west-2.on.aws';
const FIS_CORS_PROXY = 'https://p474fmngovil3jmfzxh75fuvg40ksjuf.lambda-url.us-west-2.on.aws/';

/**
 * Main FIS cleanup function.
 * @param {Array<Object>} racers - Racer data (normalized)
 * @param {Object} options - Cleanup options
 * @param {string} options.discipline - 'DI' or 'SP'
 * @param {number} options.listNumber - FIS list number
 * @param {Function} options.onProgress - Progress callback
 * @returns {Promise<Object>} { validRacers, removedRacers, changes }
 */
export async function cleanFisData(racers, options) {
  const { discipline = 'DI', listNumber, onProgress } = options;
  
  // Detect if this is a sprint heats file (has "Highest Level" data)
  const isSprintHeats = racers.some(r => 
    r['Highest Level'] || r.highestLevel || r.HighestLevel
  );
  
  console.log('[FIS Cleanup] Starting with options:', { discipline, listNumber, racerCount: racers?.length });
  
  const changes = [];
  const removedRacers = [];
  
  // Validate list number
  if (!listNumber) {
    console.warn('[FIS Cleanup] No list number provided!');
    onProgress?.('Warning: No FIS list number specified');
  }
  
  // Step 1: Separate racers with/without FIS codes
  onProgress?.('Filtering racers...');
  const racersWithFis = racers.filter(r => getField(r, CANONICAL_FIELDS.FIS_CODE));
  const racersWithoutFis = racers.filter(r => !getField(r, CANONICAL_FIELDS.FIS_CODE));
  
  console.log('[FIS Cleanup] Racers with FIS code:', racersWithFis.length, ', without:', racersWithoutFis.length);
  
  // Mark no-FIS racers as removed
  racersWithoutFis.forEach(r => {
    removedRacers.push({
      racer: r,
      reason: 'Missing FIS Code',
    });
  });
  
  if (racersWithFis.length === 0) {
    return { validRacers: [], removedRacers, changes };
  }
  
  // Step 2: Lookup FIS points
  onProgress?.(`Looking up FIS points (List ${listNumber})...`);
  const fisCodes = racersWithFis.map(r => getField(r, CANONICAL_FIELDS.FIS_CODE));
  
  let pointsData = [];
  try {
    pointsData = await fetchFisPoints(fisCodes, listNumber);
  } catch (err) {
    console.error('Error fetching FIS points:', err);
    onProgress?.('Error fetching FIS points');
  }
  
  // Create lookup map
  const pointsMap = new Map();
  pointsData.forEach(p => {
    pointsMap.set(String(p.fis_licence).trim(), p);
  });
  
  // Step 3: Find racers not in points list and look them up via CORS proxy
  const missingFromPoints = fisCodes.filter(code => !pointsMap.has(String(code).trim()));
  
  if (missingFromPoints.length > 0) {
    onProgress?.(`Looking up ${missingFromPoints.length} racers not in points list...`);
    try {
      const websiteData = await fetchFisWebsite(missingFromPoints);
      websiteData.forEach(p => {
        pointsMap.set(String(p.fis_licence).trim(), p);
      });
    } catch (err) {
      console.error('Error fetching FIS website data:', err);
    }
  }
  
  // Step 4: Apply updates
  onProgress?.('Applying updates...');
  const validRacers = [];
  
  for (const racer of racersWithFis) {
    const fisCode = String(getField(racer, CANONICAL_FIELDS.FIS_CODE)).trim();
    const apiData = pointsMap.get(fisCode);
    
    if (!apiData) {
      removedRacers.push({
        racer,
        reason: 'Not found in FIS database',
      });
      continue;
    }
    
    // Check if marked as inactive
    if (apiData.status === 'E') {
      removedRacers.push({
        racer,
        reason: 'Inactive in FIS (Status E)',
      });
      continue;
    }
    
    // Apply updates - preserve all original fields including sprint fields
    const updatedRacer = { ...racer };
    
    // Preserve sprint heats fields if present
    if (racer['Highest Level'] || racer.highestLevel) {
      updatedRacer['Highest Level'] = racer['Highest Level'] || racer.highestLevel;
    }
    if (racer['Quarter Rank'] || racer.quarterRank) {
      updatedRacer['Quarter Rank'] = racer['Quarter Rank'] || racer.quarterRank;
    }
    if (racer['Quarter Time'] || racer.quarterTime) {
      updatedRacer['Quarter Time'] = racer['Quarter Time'] || racer.quarterTime;
    }
    if (racer['Semi Rank'] || racer.semiRank) {
      updatedRacer['Semi Rank'] = racer['Semi Rank'] || racer.semiRank;
    }
    if (racer['Semi Time'] || racer.semiTime) {
      updatedRacer['Semi Time'] = racer['Semi Time'] || racer.semiTime;
    }
    
    // Name updates
    if (apiData.first_name && apiData.first_name !== getField(racer, CANONICAL_FIELDS.FIRST_NAME)) {
      changes.push({
        fisCode,
        field: 'firstName',
        oldValue: getField(racer, CANONICAL_FIELDS.FIRST_NAME),
        newValue: apiData.first_name,
      });
      updatedRacer[CANONICAL_FIELDS.FIRST_NAME] = apiData.first_name;
    }
    
    if (apiData.last_name && apiData.last_name !== getField(racer, CANONICAL_FIELDS.LAST_NAME)) {
      changes.push({
        fisCode,
        field: 'lastName',
        oldValue: getField(racer, CANONICAL_FIELDS.LAST_NAME),
        newValue: apiData.last_name,
      });
      updatedRacer[CANONICAL_FIELDS.LAST_NAME] = apiData.last_name;
    }
    
    // Nation update
    if (apiData.nation && apiData.nation !== getField(racer, CANONICAL_FIELDS.COUNTRY)) {
      changes.push({
        fisCode,
        field: 'country',
        oldValue: getField(racer, CANONICAL_FIELDS.COUNTRY),
        newValue: apiData.nation,
      });
      updatedRacer[CANONICAL_FIELDS.COUNTRY] = apiData.nation;
    }
    
    // YOB update
    const apiYOB = apiData.date_of_birth?.split('-')[0];
    if (apiYOB && apiYOB !== getField(racer, CANONICAL_FIELDS.YOB)) {
      changes.push({
        fisCode,
        field: 'yob',
        oldValue: getField(racer, CANONICAL_FIELDS.YOB),
        newValue: apiYOB,
      });
      updatedRacer[CANONICAL_FIELDS.YOB] = apiYOB;
    }
    
    // FIS Points update
    const disciplinePoints = apiData.fis_points?.[discipline];
    console.log(`[FIS Cleanup] Points for ${fisCode}: discipline=${discipline}, fis_points=`, apiData.fis_points, `extracted=${disciplinePoints}`);
    if (disciplinePoints !== undefined && disciplinePoints !== null) {
      const currentPoints = getField(racer, CANONICAL_FIELDS.FIS_POINTS);
      if (!currentPoints || String(currentPoints) !== String(disciplinePoints)) {
        changes.push({
          fisCode,
          field: 'fisPoints',
          oldValue: currentPoints,
          newValue: disciplinePoints,
        });
        updatedRacer[CANONICAL_FIELDS.FIS_POINTS] = parseFloat(disciplinePoints);
        // Also set common field names for compatibility
        updatedRacer['FISpoints'] = parseFloat(disciplinePoints);
      }
    }
    
    // Gender
    if (apiData.gender) {
      updatedRacer[CANONICAL_FIELDS.GENDER] = apiData.gender;
    }
    
    validRacers.push(updatedRacer);
  }
  
  // Step 5: Re-rank valid racers
  onProgress?.('Re-ranking...');
  if (isSprintHeats) {
    // Sprint heats: preserve original order, just reassign ranks
    reRankSprintRacers(validRacers, racers);
  } else {
    // Distance: sort by time and re-rank
    reRankRacers(validRacers);
  }
  
  return { validRacers, removedRacers, changes };
}

/**
 * Fetch FIS points from Lambda.
 */
async function fetchFisPoints(fisCodes, listNumber) {
  console.log('[FIS Cleanup] Fetching points for', fisCodes.length, 'racers from list', listNumber);
  console.log('[FIS Cleanup] FIS codes sample:', fisCodes.slice(0, 5));
  
  const formData = new FormData();
  formData.append('points_list', listNumber);
  formData.append('racers', fisCodes.join(','));
  formData.append('return_csv', 'false');
  
  const response = await fetch(FIS_POINTS_LAMBDA, {
    method: 'POST',
    body: formData,
  });
  
  if (!response.ok) {
    console.error('[FIS Cleanup] Lambda error:', response.status, response.statusText);
    throw new Error(`FIS points API error: ${response.status}`);
  }
  
  const data = await response.json();
  console.log('[FIS Cleanup] Lambda returned', Array.isArray(data) ? data.length : 'non-array', 'results');
  
  if (data.message) {
    console.error('[FIS Cleanup] Lambda error message:', data.message);
    throw new Error(data.message);
  }
  
  // Log sample of returned data
  if (Array.isArray(data) && data.length > 0) {
    console.log('[FIS Cleanup] Sample result:', data[0]);
  }
  
  return data;
}

/**
 * Fetch FIS website data via CORS proxy.
 */
async function fetchFisWebsite(fisCodes) {
  const response = await fetch(FIS_CORS_PROXY, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ FIScodes: fisCodes }),
  });
  
  if (!response.ok) {
    throw new Error(`FIS website proxy error: ${response.status}`);
  }
  
  const rawData = await response.json();
  
  // Parse XML responses
  const results = [];
  for (const [fisCode, xmlString] of Object.entries(rawData)) {
    const parsed = parseFisXml(xmlString, fisCode);
    if (parsed) {
      results.push(parsed);
    }
  }
  
  return results;
}

/**
 * Parse FIS XML response for CC sector.
 */
function parseFisXml(xmlString, requestedCode) {
  if (!xmlString) return null;
  
  const result = {
    fis_licence: requestedCode,
    first_name: null,
    last_name: null,
    gender: null,
    nation: null,
    date_of_birth: null,
    status: null,
    fis_points: { DI: null, SP: null },
  };
  
  // Simple XML parsing (looking for CC sector)
  const tags = xmlString.split('<');
  let inCCSector = false;
  
  for (const tag of tags) {
    if (tag.includes('sectorcode="CC"')) {
      inCCSector = true;
    }
    
    if (!inCCSector) continue;
    
    const content = tag.split('>')[1]?.trim();
    if (!content || content.includes('\n')) continue;
    
    if (tag.includes('fiscode>')) result.fis_licence = content;
    if (tag.includes('lastname>')) result.last_name = content;
    if (tag.includes('firstname>')) result.first_name = content;
    if (tag.includes('gender>')) result.gender = content;
    if (tag.includes('nationcode>')) result.nation = content;
    if (tag.includes('year>')) result.date_of_birth = content;
    if (tag.includes('status>')) result.status = content;
    
    // Break after finding all needed fields
    if (result.last_name && result.first_name && result.status) {
      break;
    }
  }
  
  // Verify the returned data matches what we requested
  if (String(result.fis_licence).trim() !== String(requestedCode).trim()) {
    console.warn(`FIS data mismatch: requested ${requestedCode}, got ${result.fis_licence}`);
    // Return null - code doesn't exist or returned wrong person
    return null;
  }
  
  // If we didn't find any name data, the code probably doesn't exist
  if (!result.first_name && !result.last_name) {
    console.warn(`FIS code ${requestedCode}: No person data found in response`);
    return null;
  }
  
  return result;
}

/**
 * Re-rank racers by time (for distance races).
 */
function reRankRacers(racers) {
  // Sort by time (handle various time formats)
  racers.sort((a, b) => {
    const timeA = parseTime(getField(a, CANONICAL_FIELDS.TIME));
    const timeB = parseTime(getField(b, CANONICAL_FIELDS.TIME));
    return timeA - timeB;
  });
  
  let position = 0;
  let lastTime = null;
  let lastRank = 0;
  
  for (const racer of racers) {
    const status = getField(racer, CANONICAL_FIELDS.STATUS);
    
    // Only rank finishers
    if (!status || status === '' || status === 'QLF') {
      position++;
      const currentTime = getField(racer, CANONICAL_FIELDS.TIME);
      
      if (currentTime !== lastTime) {
        racer[CANONICAL_FIELDS.RANK] = position;
      } else {
        racer[CANONICAL_FIELDS.RANK] = lastRank; // Tie
      }
      
      lastRank = racer[CANONICAL_FIELDS.RANK];
      lastTime = currentTime;
    } else {
      racer[CANONICAL_FIELDS.RANK] = ''; // DNF, DNS, etc.
      lastTime = null;
    }
  }
}

/**
 * Re-rank sprint heats racers preserving original order.
 * Sprint heats rankings are based on progression through heats (Highest Level),
 * not on time, so we preserve the original order and just reassign sequential ranks.
 * 
 * @param {Array} validRacers - Racers that passed validation (will be sorted in place)
 * @param {Array} originalRacers - Original racer array to determine order
 */
function reRankSprintRacers(validRacers, originalRacers) {
  // Create a map of FIS code to original index
  const originalOrder = new Map();
  originalRacers.forEach((racer, idx) => {
    const fisCode = String(getField(racer, CANONICAL_FIELDS.FIS_CODE) || '').trim();
    if (fisCode) {
      originalOrder.set(fisCode, idx);
    }
  });
  
  // Sort valid racers by their original position
  validRacers.sort((a, b) => {
    const fisA = String(getField(a, CANONICAL_FIELDS.FIS_CODE) || '').trim();
    const fisB = String(getField(b, CANONICAL_FIELDS.FIS_CODE) || '').trim();
    const orderA = originalOrder.get(fisA) ?? Infinity;
    const orderB = originalOrder.get(fisB) ?? Infinity;
    return orderA - orderB;
  });
  
  // Assign sequential ranks based on sorted order
  let position = 0;
  for (const racer of validRacers) {
    const status = getField(racer, CANONICAL_FIELDS.STATUS);
    
    // Only rank finishers (QLF status or empty)
    if (!status || status === '' || status === 'QLF') {
      position++;
      racer[CANONICAL_FIELDS.RANK] = position;
    } else {
      racer[CANONICAL_FIELDS.RANK] = ''; // DNF, DNS, etc.
    }
  }
  
  console.log(`[FIS Cleanup] Sprint heats re-ranking: assigned ranks 1-${position} preserving original order`);
}

/**
 * Parse time string to seconds for sorting.
 */
function parseTime(timeStr) {
  if (!timeStr) return Infinity;
  
  const str = String(timeStr).trim();
  
  // Handle MM:SS.ss or HH:MM:SS.ss
  const parts = str.split(':');
  
  if (parts.length === 1) {
    // Just seconds
    return parseFloat(parts[0]) || Infinity;
  } else if (parts.length === 2) {
    // MM:SS.ss
    return (parseFloat(parts[0]) * 60) + parseFloat(parts[1]) || Infinity;
  } else if (parts.length === 3) {
    // HH:MM:SS.ss
    return (parseFloat(parts[0]) * 3600) + (parseFloat(parts[1]) * 60) + parseFloat(parts[2]) || Infinity;
  }
  
  return Infinity;
}

// Export individual functions for reuse
export { fetchFisPoints, fetchFisWebsite };

const fisCleanup = {
  cleanFisData,
  parseTime,
  reRankRacers,
  reRankSprintRacers,
  fetchFisPoints,
  fetchFisWebsite,
};

export default fisCleanup;
